import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type Blocked = { id: string; name: string; count: number; portfolios: string[] };
type Deletable = { id: string; name: string };

interface AssetDeletionDialogsProps {
  selectedAssetsCount: number;

  showDeleteConfirm: boolean;
  setShowDeleteConfirm: (open: boolean) => void;
  confirmDelete: () => void | Promise<void>;

  showMixedDeleteConfirm: boolean;
  setShowMixedDeleteConfirm: (open: boolean) => void;
  mixedDeleteInfo: { manual: number; connected: number; account: number };
  confirmDeleteManualOnly: () => void | Promise<void>;

  showCascadeConfirm: boolean;
  setShowCascadeConfirm: (open: boolean) => void;
  cascadeBlocked: Blocked[];
  cascadeDeletable: Deletable[];
  cascadeShowAll: boolean;
  setCascadeShowAll: (show: boolean) => void;
  confirmCascadeDelete: () => void | Promise<void>;
}

export function AssetDeletionDialogs({
  selectedAssetsCount,
  showDeleteConfirm,
  setShowDeleteConfirm,
  confirmDelete,
  showMixedDeleteConfirm,
  setShowMixedDeleteConfirm,
  mixedDeleteInfo,
  confirmDeleteManualOnly,
  showCascadeConfirm,
  setShowCascadeConfirm,
  cascadeBlocked,
  cascadeDeletable,
  cascadeShowAll,
  setCascadeShowAll,
  confirmCascadeDelete,
}: AssetDeletionDialogsProps) {
  return (
    <>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assets</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedAssetsCount} asset{selectedAssetsCount !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} variant="destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mixed Selection Delete Dialog */}
      <AlertDialog open={showMixedDeleteConfirm} onOpenChange={setShowMixedDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Some assets can't be deleted</AlertDialogTitle>
            <AlertDialogDescription>
              You selected {selectedAssetsCount} asset{selectedAssetsCount !== 1 ? 's' : ''}.<br/>
              <span className="block mt-2">{mixedDeleteInfo.connected} connected and {mixedDeleteInfo.account} account asset{(mixedDeleteInfo.connected + mixedDeleteInfo.account) !== 1 ? 's are' : ' is'} not deletable.</span>
              {mixedDeleteInfo.manual > 0 ? (
                <span className="block mt-2">You can continue to delete {mixedDeleteInfo.manual} manual asset{mixedDeleteInfo.manual !== 1 ? 's' : ''}, or cancel.</span>
              ) : (
                <span className="block mt-2">No manual assets are selected, so nothing can be deleted.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteManualOnly}
              disabled={mixedDeleteInfo.manual === 0}
              variant="destructive"
              className="disabled:opacity-60"
            >
              {mixedDeleteInfo.manual > 0 ? `Delete ${mixedDeleteInfo.manual} manual asset${mixedDeleteInfo.manual !== 1 ? 's' : ''}` : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cascade Confirmation Dialog */}
      <AlertDialog
        open={showCascadeConfirm}
        onOpenChange={(open) => {
          setShowCascadeConfirm(open);
          if (!open) setCascadeShowAll(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove referenced assets?</AlertDialogTitle>
            <AlertDialogDescription>
              Some selected assets are currently used in your portfolios. Proceeding will remove their allocations from those portfolios and delete the assets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className={`${cascadeShowAll ? 'max-h-[70vh]' : 'max-h-64'} overflow-auto rounded-md border bg-muted/20 p-3 text-sm`}>
            {cascadeBlocked.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium">Impacted assets ({cascadeBlocked.length})</div>
                <ul className="list-disc pl-5 space-y-1">
                  {cascadeBlocked.slice(0, cascadeShowAll ? cascadeBlocked.length : 8).map((b) => (
                    <li key={b.id}>
                      <span className="font-medium">{b.name}</span>
                      {b.portfolios && b.portfolios.length > 0 && (
                        <span className="text-muted-foreground"> — {b.portfolios.slice(0, 3).join(', ')}{b.portfolios.length > 3 ? ` +${b.portfolios.length - 3} more` : ''}</span>
                      )}
                    </li>
                  ))}
                  {!cascadeShowAll && cascadeBlocked.length > 8 && (
                    <li>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setCascadeShowAll(true)}
                        aria-label={`Show all impacted assets (+${cascadeBlocked.length - 8} more)`}
                      >
                        +{cascadeBlocked.length - 8} more
                      </button>
                    </li>
                  )}
                  {cascadeShowAll && cascadeBlocked.length > 8 && (
                    <li>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setCascadeShowAll(false)}
                        aria-label="Show fewer"
                      >
                        Show less
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowCascadeConfirm(false); setCascadeShowAll(false); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCascadeDelete} variant="destructive">
              Remove from portfolios and delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default AssetDeletionDialogs;
